import { Button } from "@ui/components/button";
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "@ui/components/drawer";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@ui/components/empty";
import { Spinner } from "@ui/components/spinner";
import { useIsMobile } from "@ui/hooks/use-mobile";
import { cn } from "@ui/lib";
import { CheckIcon, ShoppingBasketIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useChatLayer } from "./chrome";
import { useCopy } from "./copy-context";
import { debugLog } from "./debug";
import type { ChatLabels } from "./overrides";
import { QuantityStepper, type SetQuantity } from "./ProductCardView";
import { type Basket, type BasketLine, formatMoney } from "./types";

export function BasketDrawer({
	open,
	onOpenChange,
	basket,
	retailerName,
	onQuantity,
	onCheckout,
	labels,
	showCheckout,
}: {
	labels: ChatLabels;
	/** The checkout button (`chat.showCheckout`): off when the app checks out from its own cart. */
	showCheckout: boolean;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	basket: Basket | null;
	retailerName: string;
	onQuantity: SetQuantity;
	/** "standalone" when no app is around the chat to take the cart. */
	onCheckout: () => Promise<"host" | "standalone" | "failed">;
}) {
	const copy = useCopy();
	// Inside a chat mounted inline, the drawer stays in the chat's element (`useChatLayer`).
	const layer = useChatLayer();
	// Bottom sheet on phones and inline (the chat is phone-sized there), side panel on wider
	// screens.
	const isMobile = useIsMobile() || layer !== null;
	const [checkingOut, setCheckingOut] = useState(false);
	const [handedOff, setHandedOff] = useState(false);

	return (
		<Drawer
			open={open}
			onOpenChange={(next) => {
				debugLog(
					`basket drawer ${next ? "opened" : "closed"} by the shopper`,
				);
				onOpenChange(next);
				if (!next) {
					setHandedOff(false);
				}
			}}
			swipeDirection={isMobile ? "down" : "right"}
			showSwipeHandle={isMobile}
		>
			<DrawerContent
				container={layer ?? undefined}
				className={cn(
					"bg-(--shop-card) text-(--shop-ink)",
					layer
						? // As tall as the chat allows, not the window: the chat is its containing block.
							"data-[swipe-axis=y]:[--drawer-content-max-height:calc(100%-6rem)]"
						: "sm:[--drawer-content-width:26rem]",
				)}
			>
				<DrawerHeader className="flex-row items-baseline justify-between gap-3 px-5 pt-4 text-start group-data-[swipe-axis=y]/drawer-popup:text-start">
					<DrawerTitle className="font-semibold text-lg">
						{labels.basketTitle}
					</DrawerTitle>
					<DrawerDescription className="text-sm tabular">
						{basket?.itemCount
							? copy.items(basket.itemCount)
							: copy.empty}
					</DrawerDescription>
				</DrawerHeader>

				{handedOff && basket ? (
					<HandedOff
						basket={basket}
						retailerName={retailerName}
						onBack={() => {
							setHandedOff(false);
							onOpenChange(false);
						}}
					/>
				) : basket?.lines.length ? (
					<BasketLines
						basket={basket}
						labels={labels}
						onQuantity={onQuantity}
						checkout={
							showCheckout ? (
								<CheckoutButton
									label={labels.checkout}
									checkingOut={checkingOut}
									onClick={async () => {
										debugLog("checkout button: tapped");
										setCheckingOut(true);
										try {
											if (
												(await onCheckout()) ===
												"standalone"
											) {
												setHandedOff(true);
											}
										} finally {
											setCheckingOut(false);
										}
									}}
								/>
							) : null
						}
					/>
				) : (
					<EmptyBasket title={labels.emptyBasket} />
				)}
			</DrawerContent>
		</Drawer>
	);
}

/** No app around the chat took the cart: what checkout looks like inside the retailer's app. */
function HandedOff({
	basket,
	retailerName,
	onBack,
}: {
	basket: Basket;
	retailerName: string;
	onBack: () => void;
}) {
	return (
		<div className="flex animate-shop-rise flex-col items-center gap-3 px-6 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-center">
			<span className="flex size-16 items-center justify-center rounded-full bg-(--shop-accent) text-(--shop-accent-ink)">
				<CheckIcon className="size-8" strokeWidth={3} aria-hidden />
			</span>
			<p className="font-extrabold text-xl tracking-tight">
				Your basket is ready
			</p>
			<p className="max-w-xs text-(--shop-muted) text-sm leading-snug">
				{basket.itemCount} {basket.itemCount === 1 ? "item" : "items"} ·{" "}
				{formatMoney(basket.total, basket.currency)}. Inside the{" "}
				{retailerName} app this goes straight to checkout, with delivery
				and payment as usual.
			</p>
			<Button
				size="lg"
				className="mt-2 h-12 w-full rounded-(--shop-btn) bg-(--shop-brand-soft) font-semibold text-(--shop-brand-text) hover:bg-(--shop-brand-line)"
				onClick={onBack}
			>
				Back to the chat
			</Button>
		</div>
	);
}

function EmptyBasket({ title }: { title: string }) {
	return (
		<Empty className="flex-none py-12">
			<EmptyHeader>
				<EmptyMedia variant="icon">
					<ShoppingBasketIcon />
				</EmptyMedia>
				<EmptyTitle>{title}</EmptyTitle>
				<EmptyDescription>
					Ask for a product or a recipe, then tap + on anything you
					want.
				</EmptyDescription>
			</EmptyHeader>
		</Empty>
	);
}

/** The basket's lines, what is out of stock, the total and (when shown) checkout. */
function BasketLines({
	basket,
	labels,
	onQuantity,
	checkout,
}: {
	basket: Basket;
	labels: ChatLabels;
	onQuantity: SetQuantity;
	checkout: ReactNode;
}) {
	const unavailable = basket.lines.filter((line) => !line.inStock).length;
	return (
		<>
			<ul className="flex min-h-0 flex-1 flex-col divide-y divide-border/70 overflow-y-auto overscroll-contain px-5">
				{basket.lines.map((line) => (
					<BasketRow
						key={line.productId}
						line={line}
						currency={basket.currency}
						onQuantity={onQuantity}
					/>
				))}
			</ul>
			<DrawerFooter className="gap-3 border-border/70 border-t px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
				{unavailable ? (
					<p className="text-muted-foreground text-xs">
						{unavailable === 1
							? "1 item is out of stock."
							: `${unavailable} items are out of stock.`}
					</p>
				) : null}
				<div className="flex items-baseline justify-between">
					<span className="text-muted-foreground text-sm">
						{labels.total}
					</span>
					<span className="font-semibold text-lg tabular">
						{formatMoney(basket.total, basket.currency)}
					</span>
				</div>
				{checkout}
			</DrawerFooter>
		</>
	);
}

function CheckoutButton({
	label,
	checkingOut,
	onClick,
}: {
	label: string;
	checkingOut: boolean;
	onClick: () => void;
}) {
	return (
		<Button
			size="lg"
			className="h-12 w-full rounded-(--shop-btn) bg-(--shop-checkout) font-semibold text-(--shop-on-checkout) text-base hover:bg-(--shop-cta) hover:text-(--shop-on-cta)"
			disabled={checkingOut}
			onClick={onClick}
		>
			{checkingOut ? <Spinner data-icon="inline-start" /> : null}
			{label}
		</Button>
	);
}

function BasketRow({
	line,
	currency,
	onQuantity,
}: {
	line: BasketLine;
	currency: string;
	onQuantity: SetQuantity;
}) {
	const copy = useCopy();
	return (
		<li className="flex items-center gap-3 py-3">
			{/* Like every product photo in the chat: white, whatever the scheme. A `dark:`
			    variant follows the phone, not the chat's theme, and drew a navy frame. */}
			<div className="size-14 shrink-0 overflow-hidden rounded-xl bg-white ring-1 ring-(--shop-brand-line)">
				{line.imageUrl ? (
					<img
						src={line.imageUrl}
						alt=""
						loading="lazy"
						className={cn(
							"size-full object-contain p-1.5",
							!line.inStock && "grayscale",
						)}
					/>
				) : null}
			</div>
			<div className="min-w-0 flex-1">
				<p dir="auto" className="line-clamp-2 text-sm leading-snug">
					{line.title}
				</p>
				<p
					className={cn(
						"mt-0.5 text-xs tabular",
						line.inStock
							? "text-muted-foreground"
							: "text-destructive",
					)}
				>
					{line.inStock
						? formatMoney(line.lineTotal, currency)
						: copy.outOfStock}
				</p>
			</div>
			{/* Taps here never drag the drawer (see DrawerFooter). */}
			<div data-base-ui-swipe-ignore="" className="shrink-0">
				<QuantityStepper
					title={line.title}
					quantity={line.quantity}
					onChange={(next) => onQuantity(line.productId, next)}
					tone="soft"
					canAdd={
						line.inStock &&
						// Absent on a basket a tool returned before lines carried it.
						(line.maxOrderQuantity == null ||
							line.quantity < line.maxOrderQuantity)
					}
				/>
			</div>
		</li>
	);
}
